/**
 * Native Geolocation Service — registrazione percorsi in background
 *
 * FASE 2 (Luglio 2026): Architettura GPS a tre livelli:
 *   1. @capacitor-community/background-geolocation — Android Foreground Service
 *      con FusedLocationProvider. Sopravvive a schermo spento e process death.
 *      Wrappato in backgroundGeoBridge.ts, esportato via foregroundGeoBridge.ts
 *      per retrocompatibilità con tutti i consumer esistenti.
 *   2. @capacitor/geolocation — fallback per web e quando il plugin non c'è
 *   3. navigator.geolocation — fallback ultimo per browser legacy
 *
 * Vedi docs/GPS_PLUGIN_REPLACEMENT.md per la migrazione dal plugin custom.
 */

import { Capacitor } from "@capacitor/core";
import { Geolocation, type Position } from "@capacitor/geolocation";
import { api } from "@/api/core/apiClient";
import useAuthStore from "@/store/useAuthStore";
import { ForegroundGeo } from "./foregroundGeoBridge";

// FASE 2 (Luglio 2026): @capacitor-community/background-geolocation come primario.
// Importato via foregroundGeoBridge.ts (wrapper di compatibilità che re-esporta
// da backgroundGeoBridge.ts). Vedi docs/GPS_PLUGIN_REPLACEMENT.md.
let _bgGeo: any = null;
let _bgGeoInit = false;

async function _initBgGeo() {
  if (_bgGeoInit) return;
  _bgGeoInit = true;
  if (!Capacitor.isNativePlatform()) {
    _bgGeo = {
      start: async () => false,
      stop: async () => {},
      configure: async () => {},
      isTracking: () => false,
      isAvailable: () => false,
      requestBatteryOptimizationExemption: async () => {},
    };
    return;
  }
  // Usa ForegroundGeo (custom plugin) direttamente — niente lazy import necessario
  _bgGeo = ForegroundGeo;
}

function bg(): any {
  return _bgGeo;
}

let _watching = false;
let _watchId: string | null = null;
let _listeners: Array<(pos: Position) => void> = [];
let _gpsAvailable = false;
let _flushInitDone = false;
let _nativeListenerRemover: { remove: () => void } | null = null;

// Fallback JS adaptive per @capacitor/geolocation (quando plugin nativo non c'è)
let _adaptiveIntervalMs = 3000;
let _lastFixTime = 0;

/**
 * Configura l'intervallo adattivo di campionamento GPS in fallback.
 */
export function setAdaptiveInterval(ms: number): void {
  _adaptiveIntervalMs = Math.max(1000, Math.min(ms, 60000));
}

export function getAdaptiveInterval(): number {
  return _adaptiveIntervalMs;
}

function shouldAcceptFix(): boolean {
  const now = Date.now();
  if (now - _lastFixTime < _adaptiveIntervalMs) return false;
  _lastFixTime = now;
  return true;
}

/**
 * Calcola intervallo fallback in base alla velocità (m/s).
 */
function intervalFromSpeedMps(speedMps: number | null): number {
  if (speedMps == null) return 30000;
  const kmh = speedMps * 3.6;
  if (kmh > 80) return 2000;
  if (kmh >= 40) return 5000;
  if (kmh >= 10) return 10000;
  return 30000;
}

/**
 * Inizializza listener trasversali per pendingFlush (ACK GPS data) e
 * gpsAvailability (stato GPS).
 * Chiamare UNA volta all'avvio dell'app.
 */
export function initGeoListeners(): void {
  if (_flushInitDone || !Capacitor.isNativePlatform()) return;
  _flushInitDone = true;

  // Inizializza il bridge in background
  _initBgGeo();

  // FASE 3.5: Configura il nuovo plugin background-geolocation
  // FIX CURVA-LIVE (02 Set 2026): 3m/3s per una curva del percorso
  // visibile in tempo reale anche a bassa velocità.
  bg()
    .configure({
      distanceFilter: 3,
      interval: 3000,
    })
    .catch(() => {
      /* plugin non compilato o non disponibile */
    });

  // FASE 3.5: Il nuovo plugin gestisce il flush automaticamente
  // FIX (Luglio 2026): ForegroundGeo non ha isAvailable() nativo.
  // Usa typeof check per evitare TypeError su plugin non ancora inizializzato.
  try {
    const geo = bg();
    _gpsAvailable =
      typeof geo?.isAvailable === "function"
        ? geo.isAvailable()
        : Capacitor.isNativePlatform();
  } catch {
    _gpsAvailable = Capacitor.isNativePlatform();
  }

  // Recovery: al resume il plugin nativo fa auto-restart
  // FASE 1.5 (Luglio 2026): Dopo LMK kill, il plugin potrebbe essere morto.
  // Non ci limitiamo a controllare isTracking() — riavviamo proattivamente
  // il watch se eravamo in tracking prima del kill.
  document.addEventListener(
    "resume",
    () => {
      if (_watching && !bg().isTracking()) {
        console.log(
          "[NativeGeo] Resume detected — plugin was killed, restarting watch",
        );
        _watching = false;
        // Richiama l'ultimo callback registrato per riavviare il tracking
        if (_listeners.length > 0) {
          startNativeWatch(_listeners[_listeners.length - 1]).catch((err) => {
            console.error("[NativeGeo] Failed to restart after LMK kill:", err);
          });
        }
      } else if (_watching) {
        console.log("[NativeGeo] Resume detected — plugin already tracking");
      }
    },
    false,
  );
}

export function isGpsAvailable(): boolean {
  return _gpsAvailable;
}

/**
 * Avvia il watch della posizione nativa.
 * Su Android mostra una notifica persistente mentre registra.
 */
export async function startNativeWatch(
  onPosition: (pos: Position) => void,
): Promise<void> {
  if (_watching) {
    console.log("[NativeGeo] Already watching — skipping duplicate start");
    return;
  }

  try {
    if (Capacitor.isNativePlatform()) {
      const perm = await Geolocation.requestPermissions();
      if (perm.location !== "granted") {
        throw new Error("Permesso geolocalizzazione negato");
      }
    }

    // FASE 4: Avvia il plugin nativo ForegroundGeolocation con callback
    await _initBgGeo();
    const bgStarted = await bg().start((location: any) => {
      // Il payload arriva dal bridge (@/services/backgroundGeoBridge) come
      // GpsPosition: `speed` è in km/h, `altitude`/`heading` sono i valori
      // grezzi del fix nativo.
      const builtPos = {
        coords: {
          latitude: location.latitude ?? location.lat,
          longitude: location.longitude ?? location.lng,
          accuracy: location.accuracy ?? 0,
          altitude: location.altitude ?? null,
          altitudeAccuracy: null,
          heading: location.bearing ?? location.heading ?? null,
          // FIX UNITÀ (7 Ott 2026): il bridge converte la velocità in km/h, ma
          // un GeolocationPosition la espone in m/s. Senza la conversione il
          // tachimetro e le statistiche (velocità media/max) risultavano
          // gonfiati di 3,6×.
          speed:
            typeof location.speed === "number" &&
            Number.isFinite(location.speed)
              ? location.speed / 3.6
              : null,
        },
        timestamp: location.time ?? location.timestamp ?? Date.now(),
      } as Position;
      _listeners.forEach((fn) => fn(builtPos));
      onPosition(builtPos);
    });
    if (!bgStarted) {
      console.warn(
        "[NativeGeo] Background plugin non disponibile — uso fallback @capacitor/geolocation",
      );
      // FASE 1.3: Notifica all'utente che il tracking in background non è attivo
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("rideconnect:gps-fallback", {
            detail: {
              reason: "background-unavailable",
              message:
                "Tracciamento in background non disponibile. Il percorso verrà registrato solo ad app aperta.",
            },
          }),
        );
      }
      bg()
        .requestBatteryOptimizationExemption()
        .catch(() => {});
      let hasFirstFix = false;
      let consecutiveErrors = 0;
      const MAX_CONSECUTIVE_ERRORS = 10;

      const watchId = await Geolocation.watchPosition(
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 2000 },
        (pos: Position | null, err?: any) => {
          if (err) {
            consecutiveErrors++;
            console.warn(
              `[NativeGeo] watchPosition error (#${consecutiveErrors}):`,
              typeof err === "object" ? JSON.stringify(err) : err,
            );
            if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS && !hasFirstFix) {
              const errMsg =
                typeof err === "object" && err?.message
                  ? err.message
                  : "Impossibile ottenere la posizione GPS";
              console.error(
                "[NativeGeo] GPS watchdog: troppi errori consecutivi senza fix",
                errMsg,
              );
            }
            return;
          }
          if (pos) {
            if (!shouldAcceptFix()) return;

            setAdaptiveInterval(intervalFromSpeedMps(pos.coords.speed));
            consecutiveErrors = 0;
            hasFirstFix = true;
            _listeners.forEach((fn) => fn(pos));
            onPosition(pos);
          }
        },
      );
      _watchId = watchId;
    }

    _watching = true;

    // Il nuovo plugin gestisce automaticamente il sync
  } catch (err: any) {
    console.error("[NativeGeo] startNativeWatch failed:", err?.message || err);
    throw err;
  }
}

/**
 * Ferma il watch della posizione nativa.
 * Rimuove la notifica persistente.
 */
export async function stopNativeWatch(): Promise<void> {
  if (!_watching) return;

  try {
    await bg().stop();

    if (_nativeListenerRemover) {
      try {
        _nativeListenerRemover.remove();
      } catch {
        /* ignore */
      }
      _nativeListenerRemover = null;
    }

    if (_watchId) {
      await Geolocation.clearWatch({ id: _watchId as string });
      _watchId = null;
    }

    _watching = false;
    _listeners = [];
  } catch (err: any) {
    console.error("[NativeGeo] stopNativeWatch failed:", err?.message || err);
  }
}

export function isWatching(): boolean {
  return _watching;
}

export function addPositionListener(fn: (pos: Position) => void): () => void {
  _listeners.push(fn);
  return () => {
    _listeners = _listeners.filter((f) => f !== fn);
  };
}

export function isNativeGeoAvailable(): boolean {
  return Capacitor.isNativePlatform();
}

export async function getCurrentNativePosition(): Promise<{
  lat: number;
  lng: number;
  accuracy: number;
} | null> {
  try {
    if (!Capacitor.isNativePlatform()) return null;
    const perm = await Geolocation.requestPermissions();
    if (perm.location !== "granted") return null;

    const pos = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 10000,
    });
    return {
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
    };
  } catch {
    return null;
  }
}
